/* eslint-disable jsx-a11y/anchor-is-valid */
import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom"; // CHANGED FROM NEXT/LINK

export default function About() {
  const stats = [
    { label: "Founded", value: "2011" },
    { label: "B2B Partners", value: "500+" },
    { label: "Technical Staff", value: "25+" },
    { label: "Client Satisfaction", value: "99%" },
  ];

  return (
    <div className="w-full bg-white selection:bg-blue-600 selection:text-white font-sans overflow-x-hidden">
      
      {/* 1. PREMIUM HERO SECTION */}
      <section className="relative pt-12 pb-20 lg:pt-20 lg:pb-32 px-6 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            
            {/* Left Content */}
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-100 mb-6">
                <span className="w-2 h-2 rounded-full bg-blue-600 animate-pulse"></span>
                <span className="text-blue-700 font-bold uppercase tracking-widest text-xs">
                  Raipur's IT Leader Since 2011
                </span>
              </div>
              
              <h1 className="text-4xl lg:text-7xl font-extrabold text-slate-900 leading-tight mb-8">
                About <br />
                <span className="text-blue-600">PC World Raipur</span>
              </h1>
              
              <div className="space-y-6">
                <p className="text-lg lg:text-xl text-slate-700 font-semibold leading-relaxed italic border-l-4 border-blue-600 pl-6">
                  At PC World Raipur, we are more than just a technology store — we are your trusted partner in navigating the ever-evolving digital landscape.
                </p>
                <p className="text-base lg:text-lg text-slate-600 leading-loose">
                  Established with a vision to bridge the gap between cutting-edge technology and everyday users, PC World Raipur has grown into a premier provider of comprehensive IT solutions, serving both individual consumers and businesses across Chhattisgarh and beyond.
                </p>
              </div>
            </motion.div>

            {/* Right Image */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="relative z-10 rounded-[2.5rem] overflow-hidden shadow-2xl border-8 border-white">
                <img 
                  src="https://ik.imagekit.io/igxrggga8/pcworldraipur.in/Banners%20frontsite/Product%20Maintenance%20&%20AMC%20Support.png" 
                  alt="IT Maintenance and Support"
                  className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-700"
                />
              </div>
              {/* Abstract decorative elements */}
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-blue-100 rounded-full mix-blend-multiply filter blur-xl opacity-70"></div>
              <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-purple-100 rounded-full mix-blend-multiply filter blur-xl opacity-70"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 2. STATS GRID */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, i) => (
              <div key={i} className="p-8 rounded-3xl bg-slate-50 border border-slate-100 text-center hover:bg-white hover:shadow-xl hover:border-blue-200 transition-all duration-300">
                <h3 className="text-4xl font-extrabold text-blue-600 mb-2">{stat.value}</h3>
                <p className="text-sm font-bold text-slate-500 uppercase tracking-widest">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. MISSION & VISION (CARDS DESIGN) */}
      <section className="py-24 px-6 bg-slate-50/50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            
            <div className="relative rounded-[3rem] overflow-hidden shadow-xl aspect-video md:aspect-auto">
              <img 
                src="https://ik.imagekit.io/igxrggga8/pcworldraipur.in/Banners%20frontsite/mission.webp" 
                alt="PC World Mission and Vision"
                className="w-full h-full object-cover"
              />
            </div>

            <div className="space-y-8">
              {/* Mission Card */}
              <motion.div 
                whileInView={{ opacity: 1, x: 0 }}
                initial={{ opacity: 0, x: 30 }}
                className="bg-white p-10 rounded-[2rem] shadow-sm border border-slate-100 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-blue-600 rounded-2xl flex items-center justify-center text-2xl">🎯</div>
                  <h2 className="text-3xl font-extrabold text-slate-900 tracking-tight">Our Mission</h2>
                </div>
                <p className="text-lg text-slate-600 leading-relaxed">
                  Our mission is simple yet powerful: to deliver <span className="font-bold text-slate-900">reliable, innovative, and cost-effective IT solutions</span> that empower our customers to thrive in a digital-first world. Whether you're a student, a gamer, or a business, we make technology work for you.
                </p>
              </motion.div>

              {/* Vision Card */}
              <motion.div 
                whileInView={{ opacity: 1, x: 0 }}
                initial={{ opacity: 0, x: 30 }}
                transition={{ delay: 0.2 }}
                className="bg-white p-10 rounded-[2rem] shadow-sm border border-slate-100 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-indigo-600 rounded-2xl flex items-center justify-center text-2xl">🌟</div>
                  <h2 className="text-3xl font-extrabold text-slate-900 tracking-tight">Our Vision</h2>
                </div>
                <p className="text-lg text-slate-600 leading-relaxed">
                  To be the <span className="font-bold text-slate-900">most trusted and innovative IT solutions provider in Central India</span>, known for our integrity, service excellence, and commitment to customer success.
                </p>
              </motion.div>
            </div>

          </div>
        </div>
      </section>

      {/* 4. FINAL CTA */}
      <section className="py-24 px-6">
        <div className="max-w-5xl mx-auto rounded-[3.5rem] bg-gradient-to-r from-blue-600 to-indigo-700 p-12 lg:p-20 text-center text-white relative overflow-hidden shadow-2xl">
          <div className="relative z-10">
            <h2 className="text-4xl lg:text-6xl font-extrabold mb-8 tracking-tight">
              Ready to Upgrade?
            </h2>
            <p className="text-xl text-blue-50 mb-12 max-w-2xl mx-auto">
              Connect with Raipur's leading IT experts today for personalized consultations and genuine products.
            </p>
            <Link 
              to="/contact" 
              className="bg-white text-blue-600 px-12 py-5 rounded-full font-bold text-lg hover:bg-slate-900 hover:text-white transition-all transform hover:scale-105 inline-block shadow-lg"
            >
              Get in Touch
            </Link>
          </div>
          {/* Decorative Circles */}
          <div className="absolute top-0 left-0 w-64 h-64 bg-white/10 rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-64 h-64 bg-blue-900/20 rounded-full translate-x-1/2 translate-y-1/2 blur-3xl"></div>
        </div>
      </section>

    </div>
  );
}